import authNative from "@react-native-firebase/auth";

export type PhoneVerificationErrorCode =
  | "NO_SIGNED_IN_USER"
  | "INVALID_PHONE"
  | "CAPTCHA_FAILED"
  | "TOO_MANY_REQUESTS"
  | "QUOTA_EXCEEDED"
  | "INVALID_CODE"
  | "CODE_EXPIRED"
  | "PHONE_ALREADY_IN_USE"
  | "SEND_FAILED"
  | "VERIFY_FAILED";

export class PhoneVerificationError extends Error {
  code: PhoneVerificationErrorCode;
  cause?: unknown;
  constructor(
    code: PhoneVerificationErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message);
    this.name = "PhoneVerificationError";
    this.code = code;
    this.cause = cause;
  }
}

export type PhoneVerificationConfirmation = {
  confirm: (code: string) => Promise<void>;
};

export const mapSendError = (error: unknown): PhoneVerificationError => {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case "auth/invalid-phone-number":
      return new PhoneVerificationError(
        "INVALID_PHONE",
        "Invalid phone number.",
        error,
      );
    case "auth/captcha-check-failed":
    case "auth/missing-app-token":
    case "auth/app-not-authorized":
      return new PhoneVerificationError(
        "CAPTCHA_FAILED",
        "Device verification failed.",
        error,
      );
    case "auth/too-many-requests":
      return new PhoneVerificationError(
        "TOO_MANY_REQUESTS",
        "Too many requests. Try again later.",
        error,
      );
    case "auth/quota-exceeded":
      return new PhoneVerificationError(
        "QUOTA_EXCEEDED",
        "SMS quota exceeded for this project.",
        error,
      );
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
    case "auth/provider-already-linked":
      return new PhoneVerificationError(
        "PHONE_ALREADY_IN_USE",
        "Phone number is already linked to another account.",
        error,
      );
    default:
      return new PhoneVerificationError(
        "SEND_FAILED",
        "Failed to send verification code.",
        error,
      );
  }
};

export const mapConfirmError = (error: unknown): PhoneVerificationError => {
  const code = (error as { code?: string })?.code;
  switch (code) {
    case "auth/invalid-verification-code":
      return new PhoneVerificationError(
        "INVALID_CODE",
        "Invalid verification code.",
        error,
      );
    case "auth/code-expired":
      return new PhoneVerificationError(
        "CODE_EXPIRED",
        "Verification code expired.",
        error,
      );
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return new PhoneVerificationError(
        "PHONE_ALREADY_IN_USE",
        "Phone number is already linked to another account.",
        error,
      );
    default:
      return new PhoneVerificationError(
        "VERIFY_FAILED",
        "Failed to verify code.",
        error,
      );
  }
};

// Native counterpart of utils/phoneVerification.web.ts. Uses RN Firebase's
// signInWithPhoneNumber to send the SMS and extract the verificationId, then
// builds a PhoneAuthCredential manually and calls linkWithCredential /
// updatePhoneNumber on the already-signed-in user. We never call
// confirmationResult.confirm() — that would sign the phone-user in and
// clobber the current session. The SMS-send side-effect is the only thing
// we want from signInWithPhoneNumber; the linking is done via the credential.
export const startPhoneVerification = async (
  phoneE164: string,
): Promise<PhoneVerificationConfirmation> => {
  const user = authNative().currentUser;
  if (!user) {
    throw new PhoneVerificationError(
      "NO_SIGNED_IN_USER",
      "Must be signed in to verify a phone number.",
    );
  }

  let verificationId: string | null;
  try {
    const confirmation = await authNative().signInWithPhoneNumber(phoneE164);
    verificationId = confirmation.verificationId;
  } catch (error) {
    throw mapSendError(error);
  }

  if (!verificationId) {
    throw new PhoneVerificationError(
      "SEND_FAILED",
      "Verification ID missing from Firebase response.",
    );
  }

  const stashedVerificationId = verificationId;

  return {
    confirm: async (code: string) => {
      try {
        const credential = authNative.PhoneAuthProvider.credential(
          stashedVerificationId,
          code,
        );
        if (user.phoneNumber) {
          await user.updatePhoneNumber(credential);
        } else {
          await user.linkWithCredential(credential);
        }
      } catch (error) {
        throw mapConfirmError(error);
      }
    },
  };
};
